/**
 * @file PostVaccineAntiHbsListView.jsx
 * @author Daniel Aguirre
 * @description PostVaccine AntiHbs List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  joinArrayObject,
  removeNullValuesFromObject,
  formatDate,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  PAGE_LIMIT,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function PostVaccineAntiHbsListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);

  const additionalColumns = [
    {
      title: translate('dashboardStaff.dateLastApplication3rdDose'),
      dataIndex: 'fecha_ultima_dosis3',
      filter: {
        listName: 'lista_fecha_ultima_3ra_dosis',
        searchName: 'filterFechaUltima3raDosis',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_dosis3'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_3ra_dosis',
            searchName: 'filterFechaUltima3raDosis'
          },
          dataIndex: 'fecha_ultima_dosis3'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const { getPostVaccineAntiHbsList } = dashboardStaffActions;

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterClasificacionSeleccionada: classification,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getPostVaccineAntiHbsList(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_dosis3: formatDate(item?.fecha_ultima_dosis3)
          };
        });
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
PostVaccineAntiHbsListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getPostVaccineAntiHbsList: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default PostVaccineAntiHbsListView;
